import { Flex, Text, Paper, Skeleton, PaperProps, FlexProps } from "@mantine/core";
import { useEffect, useState, ReactNode, useCallback } from "react";
import styled from "styled-components";
import {
  getLiveAverageWork,
  getLiveOvertimeCount,
  getLiveTotalEmployee,
  getLiveTotalTodayEmployee,
} from "../../api/dashboardApi";
import {
  UsersIcon,
  IdentificationIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import SvgIcon from "../common/Icons/SvgIcon";
import { theme } from "../../theme";
import { ReactComponent as ClockErrorIcon } from "../../assets/clockError.svg";
import { useFilterContext } from "../../context/DashboardFilterContext";

interface StyledCardProps extends PaperProps {
  children?: ReactNode;
  flex?: any;
}

const DashboardStatCard = ({
  title,
  value,
  icon,
  isLoading,
}: {
  title: string;
  value: any;
  icon: React.ReactNode;
  isLoading: boolean;
}) => (
  <StyledInfoCard flex={1}>
    <Flex direction="column">
      <IconWrapper>{icon}</IconWrapper>
      <LabelText mb={6}>{title}</LabelText>
      {isLoading ? (
        <Skeleton height={32} width="60%" radius="md" />
      ) : (
        <ValueText>
          {typeof value === "object" && value !== null
            ? (value.mainValue ?? value.count ?? value.total ?? "0")
            : (value ?? "0")}
          <span style={{ fontSize: "14px", color: "#94a3b8", marginLeft: "6px", fontWeight: 600 }}>
            {value?.unit ?? ""}
          </span>
        </ValueText>
      )}
    </Flex>
  </StyledInfoCard>
);


const InfoBoxes = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [data, setData] = useState<any>({
    totalEmployee: null,
    averageWork: null,
    overtimeCount: null,
    totalToday: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [emp, avg, over, today] = await Promise.all([
        getLiveTotalEmployee(filters),
        getLiveAverageWork(filters),
        getLiveOvertimeCount(filters),
        getLiveTotalTodayEmployee(filters),
      ]);
      setData({
        totalEmployee: emp?.success ? emp.data : 0,
        averageWork: avg?.success ? avg.data : 0,
        overtimeCount: over?.success ? over.data : 0,
        totalToday: today?.success ? today.data : 0,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <StyledFlex
      direction="column"
      gap={12}
      style={{ flex, height: "100%" }}
    >
      <StyledFlex
        gap={12}
        align="stretch"
        style={{ flex: 1, minHeight: 0 }}
      >
        <DashboardStatCard
          title="Toplam Çalışan"
          icon={<UsersIcon width={22} />}
          value={data.totalEmployee}
          isLoading={loading}
        />
        <DashboardStatCard
          title="Bugün Aktif"
          icon={<IdentificationIcon width={22} />}
          value={data.totalToday}
          isLoading={loading}
        />
      </StyledFlex>

      <StyledFlex
        gap={12}
        align="stretch"
        style={{ flex: 1, minHeight: 0 }}
      >
        <DashboardStatCard
          title="Ort. Çalışma"
          icon={<ClockIcon width={22} />}
          value={data.averageWork}
          isLoading={loading}
        />
        <DashboardStatCard
          title="Fazla Mesai"
          icon={<SvgIcon color={theme.colors.brand.normal} Icon={ClockErrorIcon} width={22} />}
          value={data.overtimeCount}
          isLoading={loading}
        />
      </StyledFlex>
    </StyledFlex>
  );
};

export default InfoBoxes;

const StyledInfoCard = styled(Paper)<StyledCardProps>`
  padding: 20px;
  border-radius: 12px;
  background: white;
  border: 1px solid #e3e8ee;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  min-height: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    border-color: ${theme.colors.brand.normal};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 0;
    background: ${theme.colors.brand.normal};
    transition: height 0.3s ease;
  }

  &:hover::before {
    height: 100%;
  }
`;

const IconWrapper = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.brand.normal}10;
  color: ${theme.colors.brand.normal};
  margin-bottom: 14px;
`;

const ValueText = styled(Text)<{ children?: ReactNode }>`
  font-size: 26px;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.5px;
  line-height: 1.1;
`;

const LabelText = styled(Text)<{ children?: ReactNode; mb?: any }>`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const StyledFlex = styled(Flex)<{ children?: ReactNode } & FlexProps>``;
